import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogPopup,
  DialogTitle,
  Icon,
} from "@salestrak/ui";

import { MANAGE_DIALOG_SURFACE } from "#app/lib/constants/manage-dialog";
import {
  EditorState,
  RangeSetBuilder,
  StateEffect,
  type Extension,
} from "@codemirror/state";
import {
  Decoration,
  type DecorationSet,
  drawSelection,
  EditorView,
  ViewPlugin,
  type ViewUpdate,
  WidgetType,
  keymap,
} from "@codemirror/view";
import {
  bracketMatching,
  HighlightStyle,
  indentOnInput,
  syntaxHighlighting,
} from "@codemirror/language";
import {
  defaultKeymap,
  history,
  historyKeymap,
  indentWithTab,
} from "@codemirror/commands";
import { json } from "@codemirror/lang-json";
import { linter, type Diagnostic } from "@codemirror/lint";
import { tags as t } from "@lezer/highlight";

import { InsertVariableMenu } from "./variable-text-input";
import type { VariableGroup, VariableLabelResolver } from "./automation-variables";
import type { Omittable } from "@salestrak/ui";
import { at } from "#app/lib/utils/at";

// `{{ path }}` template reference (optional surrounding whitespace), capturing
// the inner path. Kept in sync with the backend reference regex.
const VARIABLE_RE = /\{\{\s*([^}]*?)\s*\}\}/g;

// ── Variable chips ─────────────────────────────────────────────────────────
// `{{ path }}` spans are replaced by an atomic widget showing the friendly
// label, so authors edit JSON around chips instead of raw mustache syntax.
class VariableWidget extends WidgetType {
  // Assigned in the body rather than declared as constructor parameters:
  // `erasableSyntaxOnly` forbids parameter properties, which emit assignments
  // rather than erasing.
  readonly path: string;
  readonly label: string;

  constructor(path: string, label: string) {
    super();
    this.path = path;
    this.label = label;
  }
  eq(other: VariableWidget) {
    return other.path === this.path && other.label === this.label;
  }
  toDOM() {
    const span = document.createElement("span");
    span.className = "cm-variable-chip";
    span.textContent = this.label;
    span.setAttribute("data-path", this.path);
    return span;
  }
  ignoreEvent() {
    return false;
  }
}

function buildChipDecorations(
  view: EditorView,
  labelFor: VariableLabelResolver,
): DecorationSet {
  const builder = new RangeSetBuilder<Decoration>();
  for (const { from, to } of view.visibleRanges) {
    const text = view.state.doc.sliceString(from, to);
    let match: RegExpExecArray | null;
    VARIABLE_RE.lastIndex = 0;
    while ((match = VARIABLE_RE.exec(text))) {
      const path = at(match, 1, "variable path").trim();
      // Only KNOWN variables become chips — an unknown reference stays raw
      // mustache text, matching the chip inputs' conversion rules.
      const label = labelFor(path);
      if (label === null) continue;
      const start = from + match.index;
      const end = start + match[0].length;
      builder.add(
        start,
        end,
        Decoration.replace({ widget: new VariableWidget(path, label) }),
      );
    }
  }
  return builder.finish();
}

// Dispatched when the label resolver changes so chips relabel without an edit.
const relabelEffect = StateEffect.define<null>();

function chipPlugin(labelForRef: { current: VariableLabelResolver }): Extension {
  const plugin = ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;
      constructor(view: EditorView) {
        this.decorations = buildChipDecorations(view, labelForRef.current);
      }
      update(u: ViewUpdate) {
        const relabel = u.transactions.some((tr) =>
          tr.effects.some((e) => e.is(relabelEffect)),
        );
        if (u.docChanged || u.viewportChanged || relabel) {
          this.decorations = buildChipDecorations(u.view, labelForRef.current);
        }
      }
    },
    {
      decorations: (v) => v.decorations,
      // Make the replaced spans atomic: arrows skip them, backspace removes the
      // whole reference rather than landing inside the mustache syntax.
      provide: (p) =>
        EditorView.atomicRanges.of(
          (view) => view.plugin(p)?.decorations ?? Decoration.none,
        ),
    },
  );
  return plugin;
}

// ── JSON validation (template-aware) ────────────────────────────────────────
// The body is a JSON *template*: `{{ }}` refs aren't valid JSON literally, so
// mask each with an equal-length numeric token (preserving offsets 1:1) before
// parsing, then surface any parse error at its mapped position.
function maskTemplate(text: string): string {
  return text.replace(VARIABLE_RE, (m) => "1".repeat(m.length));
}

function errorOffset(message: string, doc: string): number {
  const byPos = message.match(/position (\d+)/i);
  if (byPos) return Number(byPos[1]);
  const byLineCol = message.match(/line (\d+) column (\d+)/i);
  if (byLineCol) {
    const lines = doc.split("\n");
    const line = Math.max(0, Number(byLineCol[1]) - 1);
    let offset = 0;
    for (const text of lines.slice(0, line)) offset += text.length + 1;
    return offset + Math.max(0, Number(byLineCol[2]) - 1);
  }
  return 0;
}

function jsonTemplateLinter(view: EditorView): Diagnostic[] {
  const text = view.state.doc.toString();
  if (!text.trim()) return [];
  try {
    JSON.parse(maskTemplate(text));
    return [];
  } catch (err) {
    const message = err instanceof Error ? err.message : "Invalid JSON";
    const len = text.length;
    const at = Math.min(Math.max(0, errorOffset(message, text)), len);
    return [
      {
        from: at,
        to: Math.min(at + 1, len),
        severity: "error",
        message,
      },
    ];
  }
}

// Theme-aware JSON syntax colors (CSS vars resolve per light/dark theme).
const jsonHighlight = HighlightStyle.define([
  { tag: t.propertyName, color: "var(--info)" },
  { tag: t.string, color: "var(--success)" },
  { tag: t.number, color: "var(--warning)" },
  { tag: [t.bool, t.null], color: "var(--accent)" },
  { tag: [t.brace, t.bracket, t.separator, t.punctuation], color: "var(--muted-foreground)" },
]);

const editorTheme = EditorView.theme({
  "&": {
    fontSize: "var(--text-sm, 14px)",
    color: "var(--foreground)",
    backgroundColor: "transparent",
  },
  "&.cm-focused": { outline: "none" },
  ".cm-scroller": {
    fontFamily:
      "var(--font-mono, ui-monospace, SFMono-Regular, Menlo, Consolas, monospace)",
    lineHeight: "1.6",
  },
  ".cm-content": {
    padding: "8px 10px",
    minHeight: "120px",
    caretColor: "var(--foreground)",
  },
  ".cm-gutters": { display: "none" },
  ".cm-cursor, .cm-dropCursor": { borderLeftColor: "var(--foreground)" },
  ".cm-selectionBackground": {
    backgroundColor: "color-mix(in oklab, var(--primary) 25%, transparent)",
  },
  ".cm-matchingBracket": {
    backgroundColor: "color-mix(in oklab, var(--primary) 20%, transparent)",
    outline: "1px solid color-mix(in oklab, var(--primary) 40%, transparent)",
  },
  ".cm-tooltip": {
    backgroundColor: "var(--popover)",
    color: "var(--popover-foreground)",
    border: "1px solid var(--border)",
    borderRadius: "6px",
  },
  ".cm-variable-chip": {
    display: "inline-flex",
    alignItems: "center",
    height: "18px",
    padding: "0 8px",
    margin: "0 1px",
    borderRadius: "9999px",
    fontSize: "var(--text-2xs, 11px)",
    whiteSpace: "nowrap",
    // Mirror the soft-brand pill Badge used by VariableChip: vibrant brand
    // foreground on the opaque brand pastel. Borderless to match the chip's default.
    color: "var(--primary-subtle-foreground)",
    backgroundColor: "var(--primary-subtle)",
    border: "1px solid transparent",
  },
});

// Fullscreen variant: let the editor fill its container and scroll internally
// instead of growing to fit content.
const fillTheme = EditorView.theme({
  "&": { height: "100%" },
  ".cm-scroller": { height: "100%", overflow: "auto" },
  ".cm-content": { minHeight: "100%" },
});

// A JSON code editor for templatable webhook bodies: syntax highlighting,
// template-aware validation, bracket matching/auto-indent, and `{{ }}` refs
// rendered as friendly chips (inserted via the catalog menu). Mirrors
// VariableTextInput.
export function JsonVariableEditor({
  value,
  onChange,
  groups,
  labelFor,
  fill = false,
  expandable = true,
}: Omittable<{
  value: string;
  onChange: (next: string) => void;
  groups: VariableGroup[];
  labelFor: VariableLabelResolver;
  // Fill the parent (used inside the fullscreen dialog) and scroll internally.
  fill?: boolean;
  // Show the floating "open fullscreen" affordance + dialog. Off for the
  // editor instance rendered inside the dialog itself.
  expandable?: boolean;
}>) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const viewRef = useRef<EditorView | null>(null);
  const fillRef = useRef(fill);
  const [expanded, setExpanded] = useState(false);

  // Refs so the (mount-once) editor always calls the latest props.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const labelForRef = useRef(labelFor);
  labelForRef.current = labelFor;
  const lastValueRef = useRef(value);
  const initialValueRef = useRef(value);

  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;

    const view = new EditorView({
      parent: el,
      state: EditorState.create({
        doc: initialValueRef.current,
        extensions: [
          history(),
          drawSelection(),
          keymap.of([...defaultKeymap, ...historyKeymap, indentWithTab]),
          json(),
          indentOnInput(),
          bracketMatching(),
          syntaxHighlighting(jsonHighlight),
          linter(jsonTemplateLinter),
          chipPlugin(labelForRef),
          editorTheme,
          ...(fillRef.current ? [fillTheme] : []),
          EditorView.lineWrapping,
          EditorView.updateListener.of((u) => {
            if (!u.docChanged) return;
            const next = u.state.doc.toString();
            lastValueRef.current = next;
            onChangeRef.current(next);
          }),
        ],
      }),
    });
    viewRef.current = view;
    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // Mount once; live props are read through refs.
  }, []);

  // Re-seed when the value changes from outside (switching steps), not on our
  // own keystrokes.
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    if (value === lastValueRef.current) return;
    lastValueRef.current = value;
    view.dispatch({
      changes: { from: 0, to: view.state.doc.length, insert: value },
    });
  }, [value]);

  // Relabel chips when the resolver changes (graph edits) without an edit.
  useEffect(() => {
    viewRef.current?.dispatch({ effects: relabelEffect.of(null) });
  }, [labelFor]);

  const insertVariable = (item: { path: string }) => {
    const view = viewRef.current;
    if (!view) return;
    const insert = `{{ ${item.path} }}`;
    const { from, to } = view.state.selection.main;
    view.dispatch({
      changes: { from, to, insert },
      selection: { anchor: from + insert.length },
    });
    view.focus();
  };

  const fillProp = fill ? ("cell" as const) : undefined;

  return (
    <>
      <Box gap="xs" fill={fillProp}>
        <Box direction="column" position="relative" fill={fillProp}>
          <Box
            ref={hostRef}
            border
            rounded="md"
            background="sunken"
            overflow="hidden"
            fill={fillProp}
          />
          {expandable ? (
            <Box position="absolute" top={6} right={6}>
              <Button
                aria-label="Open editor fullscreen"
                onClick={() => setExpanded(true)}
                size="icon-xs"
                variant="ghost"
              >
                <Icon name="maximize-2" size="sm" />
              </Button>
            </Box>
          ) : null}
        </Box>
        {groups.length > 0 ? (
          <Box direction="row" justify="end">
            <InsertVariableMenu groups={groups} onSelect={insertVariable} />
          </Box>
        ) : null}
      </Box>

      {expandable ? (
        <Dialog open={expanded} onOpenChange={(o) => !o && setExpanded(false)}>
          {/* A near-fullscreen surface states its own geometry, and
            * DialogPopup carries no padding of its own. */}
          <DialogPopup className={MANAGE_DIALOG_SURFACE}>
            <DialogTitle className="sr-only">Edit JSON payload</DialogTitle>
            <Box direction="column" fill="flex" padding="md">
              <JsonVariableEditor
                value={value}
                onChange={onChange}
                groups={groups}
                labelFor={labelFor}
                fill
                expandable={false}
              />
            </Box>
          </DialogPopup>
        </Dialog>
      ) : null}
    </>
  );
}
